import { Flex, Input, Select, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { Form } from "@/organisms/Form"
import { isRegex } from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.selector"
import { DestinationsField } from "./DestinationsField"

import styles from "./RuleField.module.scss"

import type { RuleValueFieldProps } from "./RuleField.types"

// The value of a rule's condition: a select of its options, free tags, an
// argument with the regex its value matches, or network destinations
export const RuleValueField = (props: RuleValueFieldProps) => {
  const { name, option, rules, placeholder, onChange, onRemove } = props
  const { t } = useTranslation("translation", {
    keyPrefix: "governance.ai.live.form.rule"
  })
  const { t: tForm } = useTranslation("translation", {
    keyPrefix: "governance.ai.form"
  })

  if (option?.input === "argument")
    return (
      <Flex gap={8} className={styles.f1}>
        <Form.Item name={[name, "name"]} rules={rules}>
          <Input placeholder={t("argumentName")} onChange={onChange} />
        </Form.Item>
        <Text type="secondary" className={styles.label}>
          {tForm("rules.scopes.regex")}
        </Text>
        <Form.Item
          name={[name, "regex"]}
          className={styles.f1}
          rules={[
            ...rules,
            {
              validator: async (_, value) => {
                if (!isRegex(value)) throw new Error(t("invalidRegex"))
              }
            }
          ]}
        >
          <Input placeholder={t("argumentRegex")} onChange={onChange} />
        </Form.Item>
      </Flex>
    )

  if (option?.input === "destinations")
    return (
      <DestinationsField
        name={name}
        onChange={onChange}
        onRemoveLast={onRemove}
      />
    )

  if (option?.input === "tags") {
    // Known values are offered, any other value can still be typed
    const suggested = !!option.options.length

    return (
      <Form.Item name={[name, "value"]} className={styles.f1} rules={rules}>
        <Select
          mode="tags"
          open={suggested ? undefined : false}
          suffixIcon={null}
          tokenSeparators={[","]}
          placeholder={t(`placeholders.${option.id}`)}
          options={suggested ? option.options : undefined}
          // An option says under its name why it cannot be picked
          optionRender={({ label, data }) => (
            <Flex vertical>
              <span>{label}</span>
              {data.description && (
                <Text type="secondary" className="sm-text-ks">
                  {data.description}
                </Text>
              )}
            </Flex>
          )}
          filterOption={(input, item) =>
            `${item?.label ?? ""} ${item?.value ?? ""}`
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          onChange={onChange}
        />
      </Form.Item>
    )
  }

  return (
    <Form.Item name={[name, "value"]} className={styles.f1} rules={rules}>
      <Select
        mode={option?.scope === "only" ? "multiple" : undefined}
        placeholder={placeholder ?? tForm("placeholder.select")}
        options={option?.options || []}
        onChange={onChange}
      />
    </Form.Item>
  )
}
